import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Buddies from '../../atoms/Buddies';
import { BUDDIES_FIT_FILL } from '../../atoms/Buddies/Buddies.constants';
import { prefersReducedMotion } from '../../_internal/usePrefersReducedMotion';
import { BUDDY_HOME_ATTRIBUTE, BUDDY_TRIP_ARC_PX, BUDDY_TRIP_MS, BUDDY_TRIP_SIZE } from './BuddyTrip.constants';
import { Runner } from './BuddyTrip.styled';

export interface BuddyTripProps {
  who: 'A' | 'I';
  /** The `data-buddy-home` names of where the buddy leaves from and runs to. */
  from: string;
  to: string;
  /** Called once the buddy is home, straight away when it can't make the trip. */
  onArrive: () => void;
  dataTestId?: string;
}

interface Route {
  left: number;
  top: number;
  dx: number;
  dy: number;
}

const centreOf = (home: string): { x: number; y: number } | null => {
  const el = document.querySelector(`[${BUDDY_HOME_ATTRIBUTE}="${home}"]`);
  const rect = el?.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) return null;
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

const BuddyTrip: React.FC<BuddyTripProps> = ({ who, from, to, onArrive, dataTestId = 'buddy-trip' }) => {
  const [route, setRoute] = useState<Route | null>(null);
  const runnerRef = useRef<HTMLDivElement | null>(null);
  const arriveRef = useRef(onArrive);

  useEffect(() => {
    arriveRef.current = onArrive;
  }, [onArrive]);

  useLayoutEffect(() => {
    const start = centreOf(from);
    const end = centreOf(to);
    if (!start || !end || prefersReducedMotion() || typeof HTMLElement.prototype.animate !== 'function') {
      arriveRef.current();
      return;
    }
    setRoute({
      left: start.x - BUDDY_TRIP_SIZE / 2,
      top: start.y - BUDDY_TRIP_SIZE / 2,
      dx: end.x - start.x,
      dy: end.y - start.y,
    });
  }, [from, to]);

  useEffect(() => {
    const runner = runnerRef.current;
    if (!route || !runner) return undefined;
    const animation = runner.animate(
      [
        { transform: 'translate(0, 0)' },
        { transform: `translate(${route.dx / 2}px, ${route.dy / 2 - BUDDY_TRIP_ARC_PX}px)` },
        { transform: `translate(${route.dx}px, ${route.dy}px)` },
      ],
      { duration: BUDDY_TRIP_MS, easing: 'ease-in-out', fill: 'forwards' },
    );
    animation.onfinish = () => arriveRef.current();
    return () => animation.cancel();
  }, [route]);

  if (!route) return null;
  return createPortal(
    <Runner ref={runnerRef} $left={route.left} $top={route.top} data-testid={dataTestId}>
      <Buddies who={who} act="run" size={BUDDY_TRIP_SIZE} fit={BUDDIES_FIT_FILL} mirrored={route.dx < 0} />
    </Runner>,
    document.body,
  );
};

export default BuddyTrip;
